import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  TextInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  CheckboxGroupInput,
  required,
  TranslatableInputs,
  maxLength,
  useDataProvider,
  useTranslate,
  useLocaleState,
} from 'react-admin';
import { EventTypeSelector } from '../event-fields/EventTypeSelector';
import { VenueSelector } from '../event-fields/VenueSelector';
import { CategorySelector } from '../event-fields/CategorySelector';
import { PAYMENT_METHODS, VALIDATION_RULES } from '../../../constants/events';
import { shouldFieldBeDisabled, getFieldHelperText } from '../../../utils/eventFormUtils';
import { slugify } from '../../../utils/string';
import { SourceLanguageBanner } from '../SourceLanguageBanner';

/**
 * Event Basic Details Tab Component
 * Shared tab for basic event information
 */
export const EventBasicDetailsTab = ({
  eventType,
  dateType,
  onEventTypeChange,
  onDateTypeChange,
  isEdit = false,
  permissions = {},
  record = {},
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const isAdmin = permissions?.crole === 'admin';
  const isOperator = permissions?.crole === 'operator';
  const isOrganisationManager = permissions?.crole === 'organisationCompanyManager';

  const paymentChoices = React.useMemo(() => {
    const raw = isEdit
      ? PAYMENT_METHODS
      : PAYMENT_METHODS.filter((method) => method.id !== 'doorcash');
    return raw.map((m) => ({
      ...m,
      name: translate(`form.payment_method_${m.id}`, { _: m.name }),
    }));
  }, [isEdit, translate, locale]);

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      {isEdit && (
        <>
          <h3>{translate('form.pre_selected_types', { _: 'Pre Selected Types' })}</h3>
          <EventTypeSelector
            eventType={eventType}
            dateType={dateType}
            onEventTypeChange={onEventTypeChange}
            onDateTypeChange={onDateTypeChange}
            disabled={true}
          />
        </>
      )}

      <h3>{translate('form.basic_details', { _: 'Basic Details' })}</h3>

      <EventNameInput />
      <EventSlugInput record={record} />

      <VenueSelector />

      <ReferenceArrayInput
        source="organisationCompanies"
        reference="organisationCompanies"
        label={translate('form.organisation_company', { _: 'Organizasyon Şirketi' })}
        defaultValue={[]}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteArrayInput
          fullWidth
          label={translate('form.organisation_company', { _: 'Organizasyon Şirketi' })}
          matchSuggestion={() => true}
          filterToQuery={(searchText) => ({ name: searchText })}
          optionText="name"
          placeholder={translate('form.select_organisation_company', {
            _: 'Organizasyon şirketi seçin…',
          })}
          disabled={isOrganisationManager}
          value={isOrganisationManager ? [permissions.rid] : undefined}
          defaultValue={isOrganisationManager ? [permissions.rid] : []}
        />
      </ReferenceArrayInput>

      <CategorySelector />

      {(isAdmin || isOperator) && isEdit && (
        <React.Fragment>
          <h3>{translate('form.event_badge', { _: 'Event Badge' })}</h3>
          <TranslatableInputs locales={['en', 'tr']} defaultLocale="en">
            <TextInput
              source="badge.label"
              label={translate('form.badge_label', { _: 'Badge Label' })}
              fullWidth
              defaultValue=""
              validate={[
                maxLength(
                  VALIDATION_RULES.BADGE_LABEL.maxLength,
                  'The label should be at most 23 characters long.',
                ),
              ]}
            />
          </TranslatableInputs>
          <TextInput
            source="badge.textColor"
            label={translate('form.label_color', { _: 'Label Color' })}
            fullWidth
            defaultValue=""
          />
          <TextInput
            source="badge.backgroundColor"
            label={translate('form.background_color', { _: 'Background Color' })}
            fullWidth
            defaultValue=""
          />
        </React.Fragment>
      )}

      <h3>{translate('form.payment_methods', { _: 'Payment Methods' })}</h3>
      <CheckboxGroupInput
        source="paymentMethods"
        defaultValue={['cc']}
        choices={paymentChoices}
        validate={required()}
      />
    </React.Fragment>
  );
};

/**
 * Event Name Input Component
 * Handles event name input with auto-capitalization
 */
const EventNameInput = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();

  const handleNameChange = (e) => {
    setValue('name', e.target.value.toLocaleUpperCase('tr-TR'));
  };

  return (
    <TextInput
      source="name"
      label={translate('form.event_name', { _: 'Event Name' })}
      fullWidth
      validate={required()}
      onBlur={handleNameChange}
    />
  );
};

const EventSlugInput = ({ record }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { setValue, control } = useFormContext();
  const name = useWatch({ control, name: 'name' });
  const slug = useWatch({ control, name: 'slug' });
  const manuallyEdited = React.useRef(Boolean(record?.slug));

  React.useEffect(() => {
    if (manuallyEdited.current) return;
    const generated = slugify(name);
    if (generated !== slug) {
      setValue('slug', generated, { shouldDirty: Boolean(name), shouldValidate: false });
    }
  }, [name, slug, setValue]);

  const validateUniqueSlug = React.useCallback(
    async (value) => {
      const normalized = slugify(value);
      if (!normalized) return 'Slug is required.';
      if (normalized !== value) {
        return 'Use only lowercase letters, numbers and hyphens.';
      }

      try {
        const { data } = await dataProvider.getList('events-v2', {
          filter: { slug: normalized },
          pagination: { page: 1, perPage: 2 },
          sort: { field: 'createdate', order: 'DESC' },
        });
        const conflict = data.some((event) => {
          const sameRecord =
            event.id === record?.id ||
            (record?.uid && (event.uid === record.uid || event.id === record.uid));
          return !sameRecord;
        });
        return conflict ? 'This slug is already used by another event.' : undefined;
      } catch {
        return 'Slug availability could not be checked.';
      }
    },
    [dataProvider, record?.id, record?.uid],
  );

  const normalizeSlug = () => {
    manuallyEdited.current = true;
    setValue('slug', slugify(slug), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <TextInput
      source="slug"
      label={translate('form.unique_event_slug', { _: 'Unique Event Slug' })}
      fullWidth
      validate={[required(), validateUniqueSlug]}
      onFocus={() => {
        manuallyEdited.current = true;
      }}
      onBlur={normalizeSlug}
      helperText={translate('form.slug_helper_text', {
        _: 'Unique URL slug: only lowercase a-z, 0-9 and hyphens. No Turkish characters.',
      })}
      inputProps={{
        autoComplete: 'off',
        spellCheck: false,
        style: { textTransform: 'lowercase' },
      }}
    />
  );
};
